import React, { useState, useMemo, useEffect } from 'react';
import '../css/events.css';

const NEW = '__new__';

function formatDate(iso) {
  if (!iso) return iso;
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    month: 'long', day: 'numeric', year: 'numeric',
  });
}

function coupleLabel(groom, bride) {
  if (!groom && !bride) return '(no couple)';
  if (!groom) return bride;
  if (!bride) return groom;
  return `${groom} & ${bride}`;
}

export default function EventsModal({ events, maxEvents = 1, currentEventId, groomName, brideName, allVenues = [], onSwitch, onCreate, onUpdate, onAddGroom, onAddBride, onRequestVenue, venueResult, onClose }) {
  const current = events.find(e => e.id === currentEventId) ?? null;

  const [selDate,   setSelDate]   = useState(current?.date ?? '');
  const [newDate,   setNewDate]   = useState('');
  const [selCouple, setSelCouple] = useState(
    current ? `${current.groom_name ?? ''}|||${current.bride_name ?? ''}` : '|||'
  );
  const [selVenue,  setSelVenue]  = useState(current?.venue ?? '');
  const [newVenue,  setNewVenue]  = useState('');
  // Snapshot of groom/bride at the moment "+New couple" was selected,
  // so buttons appear empty without touching App state or the database.
  const [snapshotGroom, setSnapshotGroom] = useState(null);
  const [snapshotBride, setSnapshotBride] = useState(null);

  useEffect(() => {
    if (venueResult) { setNewVenue(venueResult); setSelVenue(NEW); }
  }, [venueResult]);

  const activeDate  = selDate === NEW ? newDate : selDate;
  // Names effectively set for the new couple (null until user adds someone new)
  const effectiveGroom = selCouple === NEW ? (groomName !== snapshotGroom ? groomName : null) : groomName;
  const effectiveBride = selCouple === NEW ? (brideName !== snapshotBride ? brideName : null) : brideName;
  const activeCouple = selCouple === NEW
    ? `${effectiveGroom ?? ''}|||${effectiveBride ?? ''}`
    : selCouple;
  const [activeGroom, activeBride] = activeCouple.split('|||');
  const activeVenue = selVenue === NEW ? newVenue : selVenue;

  const dates = useMemo(() => {
    if (selCouple === NEW) return [];
    if (selCouple !== '|||') {
      const [g, b] = selCouple.split('|||');
      const src = events.filter(e => (e.groom_name ?? '') === g && (e.bride_name ?? '') === b);
      return [...new Set(src.map(e => e.date).filter(Boolean))].sort();
    }
    return [];
  }, [events, selCouple]);

  const couples = useMemo(() => {
    const seen = new Set();
    return events.reduce((acc, e) => {
      const key = `${e.groom_name ?? ''}|||${e.bride_name ?? ''}`;
      if (!seen.has(key) && (e.groom_name || e.bride_name)) {
        seen.add(key);
        acc.push({ key, groom: e.groom_name ?? '', bride: e.bride_name ?? '' });
      }
      return acc;
    }, []);
  }, [events]);

  const venueOptions = useMemo(() => {
    if (selCouple === NEW || selCouple === '|||') return [];
    if (activeDate) {
      const [g, b] = selCouple.split('|||');
      return [...new Set(
        events
          .filter(e => (e.groom_name ?? '') === g && (e.bride_name ?? '') === b && (e.date ?? '') === activeDate)
          .map(e => e.venue)
          .filter(Boolean)
      )];
    }
    return [];
  }, [events, selCouple, activeDate]);

  useEffect(() => {
    if (dates.length === 1) setSelDate(dates[0]);
  }, [dates]);

  useEffect(() => {
    if (venueOptions.length === 1) setSelVenue(venueOptions[0]);
  }, [venueOptions]);

  const match = useMemo(() =>
    events.find(e =>
      (e.date ?? '') === activeDate &&
      (e.groom_name ?? '') === activeGroom &&
      (e.bride_name ?? '') === activeBride &&
      (e.venue ?? '') === activeVenue
    )
  , [events, activeDate, activeGroom, activeBride, activeVenue]);

  const isCurrent = match?.id === currentEventId;
  const coupleReady = (selCouple !== '|||' && selCouple !== NEW) ||
                      (selCouple === NEW && !!(effectiveGroom || effectiveBride));

  // Existing couple selected (not new, not blank)
  const selectingExistingCouple = selCouple !== '|||' && selCouple !== NEW;
  const dateRequired = selectingExistingCouple && dates.length > 0;
  // venueOptions is filtered only when couple + date are both chosen
  const venuesAreFiltered = selectingExistingCouple && !!activeDate;
  const venueRequired = venuesAreFiltered && venueOptions.length > 0;
  const canSwitch = selectingExistingCouple &&
    (!dateRequired || !!activeDate) &&
    (!venueRequired || !!activeVenue);

  const atEventLimit = events.length >= maxEvents;
  const canCreate = !atEventLimit && selCouple === NEW && !!(effectiveGroom || effectiveBride);

  // Fallback target when no exact match: event for this couple with no date/venue
  const blankCoupleEvent = useMemo(() =>
    selectingExistingCouple && (activeGroom || activeBride)
      ? events.find(e =>
          (e.groom_name ?? '') === activeGroom &&
          (e.bride_name ?? '') === activeBride &&
          !e.date && !e.venue
        )
      : null
  , [events, selectingExistingCouple, activeGroom, activeBride]);

  return (
    <div className="events-overlay" onClick={onClose}>
      <div className="events-modal" onClick={e => e.stopPropagation()}>
        <button className="events-close" onClick={onClose}>×</button>
        <h2 className="events-title">Events</h2>
        <div style={{ fontSize: 11, color: '#999', marginBottom: 12 }}>Event ID: {currentEventId}</div>

        {/* COUPLE */}
        <div className="events-field">
          <label className="events-label">Couple</label>
          <select
            className="events-select"
            value={selCouple}
            onChange={e => {
              const v = e.target.value;
              setSelCouple(v); setSelDate(''); setSelVenue(''); setNewVenue('');
              if (v === NEW) { setSnapshotGroom(groomName); setSnapshotBride(brideName); }
            }}
          >
            {couples.filter(c => c.groom || c.bride).map(c => (
              <option key={c.key} value={c.key}>{coupleLabel(c.groom, c.bride)}</option>
            ))}
            <option value={NEW}>➕  New couple…</option>
          </select>
          {selCouple === NEW && (
            <div className="events-couple-row">
              <button
                type="button"
                className={`events-role-btn${effectiveGroom ? ' events-role-btn--set' : ''}`}
                onClick={onAddGroom}
              >
                {effectiveGroom ? `✓ ${effectiveGroom}` : '+ Add Groom'}
              </button>
              <button
                type="button"
                className={`events-role-btn${effectiveBride ? ' events-role-btn--set' : ''}`}
                onClick={onAddBride}
              >
                {effectiveBride ? `✓ ${effectiveBride}` : '+ Add Bride'}
              </button>
            </div>
          )}
        </div>

        {/* DATE */}
        <div className="events-field">
          <label className="events-label">Date</label>
          <select
            className="events-select"
            value={selDate}
            disabled={!coupleReady}
            onChange={e => { setSelDate(e.target.value); setSelVenue(''); }}
          >
            <option value="">— select —</option>
            {dates.map(d => <option key={d} value={d}>{formatDate(d)}</option>)}
            <option value={NEW}>➕  New date…</option>
          </select>
          {selDate === NEW && (
            <input
              type="date"
              className="events-input"
              value={newDate}
              onChange={e => { setNewDate(e.target.value); setSelVenue(''); }}
            />
          )}
        </div>

        {/* VENUE */}
        <div className="events-field">
          <label className="events-label">Venue</label>
          <select
            className="events-select"
            value={selVenue}
            disabled={!coupleReady}
            onChange={e => {
              const v = e.target.value;
              if (v === NEW) { setSelVenue(''); onRequestVenue?.(); }
              else setSelVenue(v);
            }}
          >
            <option value="">— select —</option>
            {venueOptions.map(v => <option key={v} value={v}>{v}</option>)}
            <option value={NEW}>➕  New venue…</option>
          </select>
          {selVenue === NEW && (
            <input
              className="events-input"
              placeholder="e.g. Grand Dynasty"
              value={newVenue}
              onChange={e => setNewVenue(e.target.value)}
            />
          )}
        </div>

        {atEventLimit && selCouple === NEW && (
          <p className="events-limit-notice">Event limit reached ({maxEvents}/{maxEvents}). Upgrade your plan to create more events.</p>
        )}

        <div className="events-actions">
          <button className="events-btn" onClick={onClose}>Cancel</button>
          {isCurrent ? (
            <span className="events-current">✓ Current Event</span>
          ) : selectingExistingCouple ? (
            <button
              className="events-btn events-btn--primary"
              disabled={!canSwitch}
              onClick={() => {
                const target = match ?? blankCoupleEvent;
                if (!target) return;
                if (!match && blankCoupleEvent && (activeDate || activeVenue)) {
                  onUpdate(blankCoupleEvent.id, { date: activeDate || null, venue: activeVenue || null });
                } else {
                  onSwitch(target.id);
                }
                onClose();
              }}
            >
              Switch to Event
            </button>
          ) : canCreate ? (
            <button
              className="events-btn events-btn--primary"
              onClick={() => {
                onCreate({
                  date: activeDate || null,
                  groom_name: activeGroom || null,
                  bride_name: activeBride || null,
                  venue: activeVenue || null,
                });
                onClose();
              }}
            >
              Create New Event
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
